import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import { isEmpty } from "utils/validationUtil";
import { getMonthName } from "utils/dateUtil";
import "./dashboards.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Fixed palette for member bar segments. The legend is hidden (it can't scale to many
// members over a long period), so colour is only for visual separation — each member's
// identity shows on hover. Indexed by position so a colour is stable across renders.
const BAR_COLORS = [
  "rgba(54, 162, 235, 0.6)",
  "rgba(255, 99, 132, 0.6)",
  "rgba(255, 206, 86, 0.6)",
  "rgba(75, 192, 192, 0.6)",
  "rgba(153, 102, 255, 0.6)",
  "rgba(255, 159, 64, 0.6)",
  "rgba(46, 204, 113, 0.6)",
  "rgba(233, 30, 99, 0.6)",
  "rgba(96, 125, 139, 0.6)",
  "rgba(121, 85, 72, 0.6)",
];

export function ActivityStatsMonthAndMemberWiseBarChart(props) {
  const [dataSets, setDataSets] = useState([]);
  const [monthYearLables, setMonthYearLables] = useState([]); // labels of bar chart

  const init = async () => {
    if (isEmpty(props.activityStatsData) || props.activityStatsData.length === 0) {
      return [];
    }

    var memberwiseMonthlyHoursMap = new Map(); //memberName is key | Map of month-year and hours
    var _monthYearArr = [];
    props?.activityStatsData?.forEach((actStat) => {
      const key = actStat.memberName;
      const monthYear = getMonthName(actStat.month).substring(0, 3) + " " + actStat.year;

      let monthYearIndex = _monthYearArr.indexOf(monthYear);
      if (monthYearIndex === -1) {
        _monthYearArr.push(monthYear);
        monthYearIndex = _monthYearArr.length - 1;
      }

      if (!memberwiseMonthlyHoursMap.has(key)) memberwiseMonthlyHoursMap.set(key, []);
      const memberHours = memberwiseMonthlyHoursMap.get(key);
      memberHours[monthYearIndex] = (memberHours[monthYearIndex] ?? 0) + actStat.monthlyHoursSpent;
    });
    return { labels: _monthYearArr, memberwiseMonthlyHoursMap: memberwiseMonthlyHoursMap };
  };

  useEffect(() => {
    init().then((obj) => {
      const _datasets = [];
      if (typeof obj.memberwiseMonthlyHoursMap !== "undefined") {
        let colorIndex = 0;
        for (const entry of obj.memberwiseMonthlyHoursMap.entries()) {
          _datasets.push({
            label: entry[0],
            data: entry[1],
            backgroundColor: BAR_COLORS[colorIndex % BAR_COLORS.length],
          });
          colorIndex++;
        }
      }
      setDataSets(_datasets);
      setMonthYearLables(obj.labels);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.activityStatsData]);

  const options = {
    plugins: {
      title: {
        fontSize: 14,
        text: props.title,
        display: true,
        font: { size: 14 },
      },
      legend: { display: false },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
      },
    },
  };

  if (Object.keys(dataSets).length === 0) return null;

  const data = {
    labels: monthYearLables,
    datasets: dataSets,
  };

  return (
    <div style={{ position: "relative", width: "100%", height: "400px" }}>
      <Bar options={options} data={data} />
    </div>
  );
}

ActivityStatsMonthAndMemberWiseBarChart.propTypes = {
  activityStatsData: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
};
